import type { InboxConversationFragment } from "@qw/graphql";
import { useLayoutEffect, useRef } from "react";
import { messageAnchorId } from "@/message/message-anchor";

import { latestThreadMessageId } from "./latest-thread-message";

/**
 * Scrolls an opened conversation to its newest message.
 *
 * Threads read oldest-first by default, so opening at the top strands the reader at the start of a
 * negotiation they already know. This fires once per conversation — a reply arriving mid-read must
 * not yank the reader off what they were reading — and only once that message has rendered, which
 * is why it looks the element up rather than holding a ref.
 *
 * Layout, not passive: a passive effect scrolls after the browser has already painted, so opening a
 * long thread flashes the top of it and jumps a frame later.
 *
 * Deliberately blind to the reading order. Under newest-first the newest message is the top of the
 * document, so this lands there rather than at the far end — and it is still needed there:
 * `Conversation` stays mounted in the same scroll container across thread switches, so a reader who
 * scrolled deep in one thread would otherwise open the next one at that same offset.
 */
export const useLandOnNewestMessage = (
  conversationId: string | undefined,
  messages: InboxConversationFragment["messages"] | undefined
) => {
  const landedConversationId = useRef<string | undefined>(undefined);

  useLayoutEffect(() => {
    const newestMessageId = latestThreadMessageId(messages);

    if (!conversationId || !newestMessageId || landedConversationId.current === conversationId) {
      return;
    }

    const newestMessage = document.getElementById(messageAnchorId(newestMessageId));

    if (!newestMessage) {
      return;
    }

    landedConversationId.current = conversationId;
    newestMessage.scrollIntoView({ block: "start" });
  }, [conversationId, messages]);
};
